import React, { useState, useEffect } from 'react';
import { db, auth } from '../firebase';
import { ref, set, push, get, query, orderByChild, equalTo } from 'firebase/database';
import { signOut } from 'firebase/auth';
import { useNavigate, Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { usePlans } from '../hooks/usePlans';
import { apiFetch } from '../utils/api';

interface Bot {
  id: string;
  businessName: string;
  phoneNumberId: string;
  accessToken: string;
  welcomeMessage: string;
  status: string;
  createdAt: number;
  allowedWebsites?: string[];
  industry?: string;
  template?: string;
  botType?: string;
}

type BotCategory = 'whatsapp' | 'facebook' | 'website' | 'voice' | 'ai' | 'email' | null;
type AIChannel = 'whatsapp' | 'website' | 'facebook' | null;
type WhatsAppSetupMode = 'meta_connect' | 'own_number' | 'self_setup' | 'test_number' | null;
type FacebookSetupMode = 'oauth' | 'we_setup' | 'self_setup' | null;

const INDUSTRY_TEMPLATES: Record<string, any> = {
  barber: {
    name: 'Barber Shop', icon: '💈',
    triggers: [
      { trigger: 'hi', responses: [{ id: '1', type: 'buttons', content: 'Welcome! How can we help you today?', buttons: [{ id: 'b1', title: 'Book Appointment', payload: 'booking' }, { id: 'b2', title: 'Our Prices', payload: 'prices' }, { id: 'b3', title: 'Location & Hours', payload: 'location' }] }] },
      { trigger: 'booking', responses: [{ id: '2', type: 'ask', content: 'What date would you like to book?', askVariable: 'booking_date', askResponses: [{ id: '3', type: 'ask', content: 'What time works best?', askVariable: 'booking_time', askResponses: [{ id: '4', type: 'text', content: "Booking request received for {{booking_date}} at {{booking_time}}! We'll confirm shortly." }] }] }] },
      { trigger: 'prices', responses: [{ id: '5', type: 'list', content: 'Our services:', listSections: [{ title: 'Haircuts', rows: [{ id: 'p1', title: "Men's Cut - $10", payload: 'men' }, { id: 'p2', title: "Kids Cut - $7", payload: 'kids' }, { id: 'p3', title: "Beard Trim - $5", payload: 'beard' }] }] }] },
      { trigger: 'location', responses: [{ id: '6', type: 'text', content: "We're at 123 Main Street, Harare. Hours: Mon-Sat, 8am-6pm" }] },
    ],
  },
  restaurant: {
    name: 'Restaurant', icon: '🍕',
    triggers: [
      { trigger: 'hi', responses: [{ id: '1', type: 'buttons', content: 'Welcome! What can we help you with?', buttons: [{ id: 'b1', title: 'View Menu', payload: 'menu' }, { id: 'b2', title: 'Book a Table', payload: 'booking' }, { id: 'b3', title: 'Our Hours', payload: 'hours' }] }] },
      { trigger: 'menu', responses: [{ id: '2', type: 'text', content: 'Our menu is available at [link]. We serve breakfast, lunch & dinner daily!' }] },
      { trigger: 'booking', responses: [{ id: '3', type: 'ask', content: 'How many people will be dining?', askVariable: 'party_size', askResponses: [{ id: '4', type: 'ask', content: 'What date and time?', askVariable: 'booking_time', askResponses: [{ id: '5', type: 'text', content: 'Table reserved for {{party_size}} on {{booking_time}}! See you then.' }] }] }] },
      { trigger: 'hours', responses: [{ id: '6', type: 'text', content: 'Open daily 7am-10pm. Kitchen closes at 9:30pm.' }] },
    ],
  },
  retail: {
    name: 'Clothing Store', icon: '👕',
    triggers: [
      { trigger: 'hi', responses: [{ id: '1', type: 'buttons', content: 'Hi there! Welcome to our store.', buttons: [{ id: 'b1', title: 'New Arrivals', payload: 'new' }, { id: 'b2', title: 'Sale Items', payload: 'sale' }, { id: 'b3', title: 'Find a Size', payload: 'sizes' }] }] },
      { trigger: 'new', responses: [{ id: '2', type: 'text', content: 'Check out our latest collection at [link]. New stock arrives every Friday!' }] },
      { trigger: 'sale', responses: [{ id: '3', type: 'text', content: 'We have up to 50% off selected items!' }] },
      { trigger: 'sizes', responses: [{ id: '4', type: 'text', content: "We stock XS-3XL. Send us your measurements and we'll help find the perfect fit." }] },
    ],
  },
  realestate: {
    name: 'Real Estate', icon: '🏠',
    triggers: [
      { trigger: 'hi', responses: [{ id: '1', type: 'buttons', content: 'Hello! Looking to buy, rent, or sell?', buttons: [{ id: 'b1', title: 'Buy / Rent', payload: 'listings' }, { id: 'b2', title: 'Sell My Property', payload: 'sell' }, { id: 'b3', title: 'Book a Viewing', payload: 'viewing' }] }] },
      { trigger: 'listings', responses: [{ id: '2', type: 'text', content: "Browse our latest listings at [link]. Tell me your budget and preferred area." }] },
      { trigger: 'viewing', responses: [{ id: '3', type: 'ask', content: 'Which property are you interested in?', askVariable: 'property', askResponses: [{ id: '4', type: 'ask', content: 'Preferred date?', askVariable: 'viewing_date', askResponses: [{ id: '5', type: 'text', content: 'Viewing booked for {{property}} on {{viewing_date}}.' }] }] }] },
      { trigger: 'sell', responses: [{ id: '6', type: 'text', content: "We'd love to help you sell. Drop your property details for a free valuation." }] },
    ],
  },
  salon: {
    name: 'Beauty Salon', icon: '💅',
    triggers: [
      { trigger: 'hi', responses: [{ id: '1', type: 'buttons', content: 'Welcome to our salon! How can we help?', buttons: [{ id: 'b1', title: 'Book a Service', payload: 'booking' }, { id: 'b2', title: 'Services & Prices', payload: 'prices' }, { id: 'b3', title: 'Contact Us', payload: 'contact' }] }] },
      { trigger: 'prices', responses: [{ id: '2', type: 'text', content: 'Hair: from $15 | Nails: from $10 | Facials: from $25.' }] },
      { trigger: 'booking', responses: [{ id: '3', type: 'ask', content: 'What service are you interested in?', askVariable: 'service', askResponses: [{ id: '4', type: 'ask', content: 'What date works for you?', askVariable: 'date', askResponses: [{ id: '5', type: 'text', content: "{{service}} booked for {{date}}!" }] }] }] },
      { trigger: 'contact', responses: [{ id: '6', type: 'text', content: 'Call us on [number] or visit us at [address].' }] },
    ],
  },
  custom: {
    name: 'Start from scratch', icon: '🎨',
    triggers: [
      { trigger: 'hi', responses: [{ id: '1', type: 'buttons', content: 'Hello! How can I help you today?', buttons: [{ id: 'b1', title: 'Contact Us', payload: 'contact' }, { id: 'b2', title: 'About Us', payload: 'about' }, { id: 'b3', title: 'Get Help', payload: 'help' }] }] },
      { trigger: 'contact', responses: [{ id: '2', type: 'text', content: "You can reach us at [email] or [phone]." }] },
      { trigger: 'about', responses: [{ id: '3', type: 'text', content: "We're [business name]. [Add your description here]." }] },
      { trigger: 'help', responses: [{ id: '4', type: 'text', content: "What do you need help with? Type your question." }] },
    ],
  },
};

const industries = [
  { id: 'barber', name: '💈 Barber Shop', description: 'Bookings, prices, location' },
  { id: 'salon', name: '💅 Beauty Salon', description: 'Services, bookings, prices' },
  { id: 'retail', name: '👕 Clothing Store', description: 'Products, sizes, stock' },
  { id: 'restaurant', name: '🍕 Restaurant', description: 'Menu, reservations, hours' },
  { id: 'realestate', name: '🏠 Real Estate', description: 'Listings, viewings, valuations' },
  { id: 'custom', name: '🎨 Start from scratch', description: 'Build your own flows' },
];

// Bot type locking is derived from plan.allowedBotTypes returned by usePlans()

function getSetupFeeDesc(cat: BotCategory, wa: WhatsAppSetupMode, fb: FacebookSetupMode): string {
  if (cat === 'whatsapp' && wa === 'own_number') return 'WhatsApp Business API – Own Number Registration';
  if (cat === 'facebook' && fb === 'we_setup') return 'Facebook Messenger Bot – Full Setup Service';
  return 'Setup Fee';
}

async function createSetupInvoice(userId: string, businessName: string, botId: string, amount: number, description: string, contactEmail: string): Promise<string> {
  const invRef = push(ref(db, `invoices/${userId}`));
  const now = Date.now();
  const d = new Date(now);
  const dateStr = `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`;
  const invoiceNumber = `INV-${dateStr}-${Math.floor(1000 + Math.random() * 9000)}`;
  await set(invRef, { invoiceNumber, botId, businessName, description, amount, status: 'unpaid', type: 'setup_fee', contactEmail, createdAt: now, dueDate: now + 7 * 24 * 60 * 60 * 1000, paidAt: null, transactionId: null, paymentMethod: null, plan: 'setup' });
  return invRef.key!;
}

const S = {
  page: { display: 'flex', minHeight: 'calc(100vh - 64px)', background: 'var(--bg-page)', fontFamily: "'DM Sans', system-ui, sans-serif" } as React.CSSProperties,
  sidebar: (c: boolean): React.CSSProperties => ({ width: c ? '72px' : '240px', background: 'var(--bg-sidebar)', borderRight: '1px solid var(--border-subtle)', transition: 'width 0.3s cubic-bezier(.4,0,.2,1)', display: 'flex', flexDirection: 'column', position: 'relative', flexShrink: 0 }),
  main: { flex: 1, overflow: 'auto', padding: '40px 32px', background: 'var(--bg-page)' } as React.CSSProperties,
  card: { background: 'var(--bg-card)', borderRadius: '16px', border: '1px solid var(--bg-element)', padding: '28px' } as React.CSSProperties,
  label: { display: 'block', fontWeight: '600', fontSize: '11px', color: 'var(--text-muted)', letterSpacing: '0.07em', textTransform: 'uppercase' as const, marginBottom: '8px' },
  input: { width: '100%', padding: '11px 14px', background: 'var(--bg-page)', border: '1px solid var(--bg-element)', borderRadius: '9px', color: 'var(--text-primary)', fontSize: '14px', outline: 'none', boxSizing: 'border-box' as const },
  btnPrimary: (disabled = false): React.CSSProperties => ({ padding: '11px 28px', background: disabled ? 'var(--bg-element)' : 'linear-gradient(135deg,#5B4FE8,#7c3aed)', color: disabled ? 'var(--text-faint)' : 'white', border: 'none', borderRadius: '9px', fontWeight: '600', cursor: disabled ? 'not-allowed' : 'pointer', fontSize: '14px', transition: 'all 0.2s' }),
  btnGhost: { padding: '11px 22px', background: 'transparent', color: 'var(--text-muted)', border: '1px solid var(--bg-element)', borderRadius: '9px', fontWeight: '500', cursor: 'pointer', fontSize: '14px' } as React.CSSProperties,
};

function StepDots({ total, current }: { total: number; current: number }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '32px' }}>
      {Array.from({ length: total }).map((_, i) => (
        <React.Fragment key={i}>
          <div style={{ width: i + 1 === current ? '24px' : '7px', height: '7px', borderRadius: '4px', background: i + 1 <= current ? '#5B4FE8' : 'var(--bg-element)', opacity: i + 1 <= current ? 1 : 0.4, transition: 'all 0.3s' }} />
          {i < total - 1 && <div style={{ flex: 1, height: '1px', background: i + 1 < current ? '#5B4FE8' : 'var(--bg-element)', opacity: 0.4 }} />}
        </React.Fragment>
      ))}
    </div>
  );
}

function BotTypeCard({ icon, label, description, badge, badgeColor = '#5B4FE8', selected, locked, lockReason, onClick }: { icon: string; label: string; description: string; badge?: string; badgeColor?: string; selected: boolean; locked?: boolean; lockReason?: string; onClick: () => void }) {
  return (
    <div onClick={locked ? undefined : onClick} style={{ padding: '24px 20px', borderRadius: '14px', cursor: locked ? 'not-allowed' : 'pointer', position: 'relative', border: selected ? '2px solid #5B4FE8' : '1px solid var(--bg-element)', background: selected ? 'rgba(91,79,232,0.10)' : locked ? 'var(--bg-page)' : 'var(--bg-card)', transition: 'all 0.15s', textAlign: 'center', opacity: locked ? 0.55 : 1 }}>
      {locked && <div style={{ position: 'absolute', top: '10px', right: '10px', background: '#f59e0b22', color: '#f59e0b', fontSize: '9px', fontWeight: '700', padding: '3px 7px', borderRadius: '20px' }}>🔒 PAID</div>}
      {badge && !locked && <div style={{ position: 'absolute', top: '12px', right: '12px', background: badgeColor + '22', color: badgeColor, fontSize: '9px', fontWeight: '700', padding: '3px 7px', borderRadius: '20px', letterSpacing: '0.06em' }}>{badge}</div>}
      {selected && !locked && <div style={{ position: 'absolute', top: '12px', left: '12px', width: '18px', height: '18px', borderRadius: '50%', background: '#5B4FE8', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><span style={{ color: 'white', fontSize: '10px' }}>✓</span></div>}
      <div style={{ fontSize: '38px', marginBottom: '12px', lineHeight: 1 }}>{icon}</div>
      <div style={{ color: locked ? 'var(--text-faint)' : 'var(--text-primary)', fontWeight: '600', fontSize: '15px', marginBottom: '5px' }}>{label}</div>
      <div style={{ color: 'var(--text-faint)', fontSize: '12px', lineHeight: '1.5' }}>{description}</div>
      {locked && lockReason && <div style={{ marginTop: '10px', fontSize: '11px', color: '#f59e0b' }}>{lockReason}</div>}
    </div>
  );
}

function SetupOptionCard({ icon, title, description, fee, feeColor = '#10b981', badge, badgeColor = '#5B4FE8', selected, locked, lockReason, onClick }: { icon: string; title: string; description: string; fee?: string; feeColor?: string; badge?: string; badgeColor?: string; selected: boolean; locked?: boolean; lockReason?: string; onClick: () => void }) {
  return (
    <div onClick={locked ? undefined : onClick} style={{ padding: '20px', borderRadius: '13px', cursor: locked ? 'not-allowed' : 'pointer', position: 'relative', border: selected ? '2px solid #5B4FE8' : '1px solid var(--bg-element)', background: selected ? 'rgba(91,79,232,0.08)' : locked ? 'var(--bg-page)' : 'var(--bg-card)', transition: 'all 0.15s', opacity: locked ? 0.55 : 1 }}>
      {locked && <div style={{ position: 'absolute', top: '10px', right: '10px', background: '#f59e0b22', color: '#f59e0b', fontSize: '9px', fontWeight: '700', padding: '3px 8px', borderRadius: '20px' }}>🔒 PAID</div>}
      {badge && !locked && <div style={{ position: 'absolute', top: '10px', right: '10px', background: badgeColor + '22', color: badgeColor, fontSize: '9px', fontWeight: '700', padding: '3px 8px', borderRadius: '20px', letterSpacing: '0.06em' }}>{badge}</div>}
      {selected && !locked && <div style={{ position: 'absolute', top: '10px', left: '10px', width: '18px', height: '18px', borderRadius: '50%', background: '#5B4FE8', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><span style={{ color: 'white', fontSize: '10px' }}>✓</span></div>}
      <div style={{ fontSize: '28px', marginBottom: '10px' }}>{icon}</div>
      <div style={{ color: locked ? 'var(--text-faint)' : 'var(--text-primary)', fontWeight: '600', fontSize: '14px', marginBottom: '5px' }}>{title}</div>
      <div style={{ color: 'var(--text-muted)', fontSize: '12px', lineHeight: '1.6', marginBottom: fee || (locked && lockReason) ? '10px' : 0 }}>{description}</div>
      {locked && lockReason ? (
        <div style={{ display: 'inline-block', background: '#f59e0b18', color: '#f59e0b', fontSize: '11px', fontWeight: '700', padding: '3px 10px', borderRadius: '20px' }}>{lockReason}</div>
      ) : fee && (
        <div style={{ display: 'inline-block', background: feeColor + '18', color: feeColor, fontSize: '11px', fontWeight: '700', padding: '3px 10px', borderRadius: '20px' }}>{fee}</div>
      )}
    </div>
  );
}

function InfoBox({ items, color = '#5B4FE8' }: { items: string[]; color?: string }) {
  return (
    <div style={{ background: color + '0d', border: `1px solid ${color}30`, borderLeft: `3px solid ${color}`, borderRadius: '10px', padding: '14px 16px', marginBottom: '20px' }}>
      {items.map((item, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: i < items.length - 1 ? '7px' : 0 }}>
          <span style={{ color, fontSize: '11px', marginTop: '4px', flexShrink: 0 }}>●</span>
          <span style={{ color: 'var(--text-secondary)', fontSize: '13px', lineHeight: '1.6' }}>{item}</span>
        </div>
      ))}
    </div>
  );
}

export default function CreateBot() {
  const navigate = useNavigate();
  const { plans, getBotLimit, getSetupFee } = usePlans();
  const [loading, setLoading] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [user, setUser] = useState<any>(null);
  const [userData, setUserData] = useState<any>(null);
  const [bots, setBots] = useState<Bot[]>([]);

  const [step, setStep] = useState(1);

  const [botCategory, setBotCategory] = useState<BotCategory>(null);
  const [aiChannel, setAIChannel] = useState<AIChannel>(null);
  const [datasets, setDatasets] = useState<Array<{ id: string; name: string; format: string }>>([]);
  const [datasetsLoading, setDatasetsLoading] = useState(false);
  const [selectedDatasetId, setSelectedDatasetId] = useState('');
  const [whatsappMode, setWhatsappMode] = useState<WhatsAppSetupMode>(null);
  const [facebookMode, setFacebookMode] = useState<FacebookSetupMode>(null);
  // Orthogonal to facebookMode (which is about HOW the Page gets connected -
  // oauth/we_setup/self_setup) - this is WHAT the bot does once connected.
  // Defaults to 'messages' so every existing flow/validation path below is
  // untouched when this is left alone.
  const [fbReplyMode, setFbReplyMode] = useState<'messages' | 'comments'>('messages');
  const [fbOAuthStatus, setFbOAuthStatus] = useState<'idle' | 'loading' | 'pages' | 'done'>('idle');
  const [fbOAuthPages, setFbOAuthPages] = useState<Array<{ id: string; name: string; access_token: string; category: string }>>([]);
  const [waOAuthStatus, setWaOAuthStatus] = useState<'idle' | 'loading' | 'numbers' | 'done'>('idle');
  const [waNumbers, setWaNumbers] = useState<Array<{ id: string; display_phone_number: string; verified_name: string }>>([]);
  const [wabaId, setWabaId] = useState('');
  const [testKeyword, setTestKeyword] = useState('');
  const [testNumberInfo, setTestNumberInfo] = useState<{ displayNumber: string | null; configured: boolean } | null>(null);

  // Email bot state
  const [emailPreset, setEmailPreset] = useState('');
  const [emailUser, setEmailUser] = useState('');
  const [emailPass, setEmailPass] = useState('');
  const [emailFrom, setEmailFrom] = useState('');
  const [imapHost, setImapHost] = useState('');
  const [imapPort, setImapPort] = useState(993);
  const [imapSecure, setImapSecure] = useState(true);
  const [smtpHost, setSmtpHost] = useState('');
  const [smtpPort, setSmtpPort] = useState(587);
  const [smtpSecure, setSmtpSecure] = useState(false);
  const [emailTestStatus, setEmailTestStatus] = useState<'idle' | 'testing' | 'ok' | 'error'>('idle');
  const [emailTestError, setEmailTestError] = useState('');

  const [businessName, setBusinessName] = useState('');
  const [industry, setIndustry] = useState('');
  const [welcomeMessage, setWelcomeMessage] = useState('');
  const [businessAddress, setBusinessAddress] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [ownPhoneNumber, setOwnPhoneNumber] = useState('');
  const [ownPhoneCountry, setOwnPhoneCountry] = useState('ZW');
  const [selfPhoneNumberId, setSelfPhoneNumberId] = useState('');
  const [selfAccessToken, setSelfAccessToken] = useState('');
  const [fbPageId, setFbPageId] = useState('');
  const [fbPageToken, setFbPageToken] = useState('');
  const [fbPageUrl, setFbPageUrl] = useState('');

  const [selectedTemplate, setSelectedTemplate] = useState('custom');
  const [firebaseTemplates, setFirebaseTemplates] = useState<any[]>([]);

  const loadFirebaseTemplates = async (botType: string) => {
    try {
      const snap = await get(ref(db, 'templates'));
      if (!snap.exists()) return;
      const list: any[] = [];
      snap.forEach(c => {
        const t = { id: c.key, ...c.val() };
        if (t.botType === botType) {
          const isOwn = auth.currentUser && t.createdBy === auth.currentUser.uid;
          if (t.isPublic !== false || isOwn) list.push(t);
        }
      });
      setFirebaseTemplates(list);
    } catch (e) { /* silent */ }
  };

  useEffect(() => {
    const unsub = auth.onAuthStateChanged(async (cu) => {
      setUser(cu);
      if (cu) {
        const snap = await get(ref(db, `users/${cu.uid}`));
        setUserData(snap.val());
        const bQ = query(ref(db, 'bots'), orderByChild('userId'), equalTo(cu.uid));
        const bS = await get(bQ);
        const list: Bot[] = [];
        bS.forEach((c) => { list.push({ id: c.key!, ...c.val() }); });
        setBots(list);
      }
    });
    return unsub;
  }, []);

  useEffect(() => {
    if (whatsappMode !== 'test_number') return;
    const apiBase = process.env.NODE_ENV === 'development' ? '' : (process.env.REACT_APP_API_URL || '');
    fetch(`${apiBase}/api/test-number/info`)
      .then(r => r.json())
      .then(d => setTestNumberInfo({ displayNumber: d.displayNumber || null, configured: d.configured || false }))
      .catch(() => {});
  }, [whatsappMode]);

  useEffect(() => {
    if (botCategory !== 'facebook' && botCategory !== 'whatsapp') return;
    if ((window as any).FB) return;
    const script = document.createElement('script');
    script.src = 'https://connect.facebook.net/en_US/sdk.js';
    script.async = true;
    script.onload = () => {
      (window as any).FB.init({ appId: process.env.REACT_APP_FACEBOOK_APP_ID || '', version: 'v21.0', xfbml: false, cookie: true });
    };
    document.head.appendChild(script);
  }, [botCategory]);

  // Meta Embedded Signup postMessage listener — captures WABA/phone data mid-wizard
  useEffect(() => {
    if (botCategory !== 'whatsapp') return;
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== 'https://www.facebook.com' && event.origin !== 'https://web.facebook.com') return;
      try {
        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        if (data.type !== 'WA_EMBEDDED_SIGNUP') return;
        if (data.event === 'FINISH') {
          const { phone_number_id, waba_id } = data.data || {};
          if (waba_id) setWabaId(waba_id);
          if (phone_number_id) setSelfPhoneNumberId(phone_number_id);
        } else if (data.event === 'CANCEL') {
          console.log('[Embedded Signup] User cancelled at step:', data.data?.current_step);
        } else if (data.event === 'ERROR') {
          console.error('[Embedded Signup] Error:', data.data?.error_message);
        }
      } catch (_) {}
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [botCategory]);

  const handleFacebookOAuth = () => {
    const FB = (window as any).FB;
    if (!FB) { alert('Facebook SDK not loaded. Please refresh and try again.'); return; }
    setFbOAuthStatus('loading');
    FB.login((response: any) => {
      if (!response.authResponse) { setFbOAuthStatus('idle'); return; }

      const apiBase = process.env.NODE_ENV === 'development' ? '' : (process.env.REACT_APP_API_URL || '');
      auth.currentUser!.getIdToken()
        .then((idToken: string) => fetch(`${apiBase}/api/bots/facebook-page-exchange`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
          body: JSON.stringify({ accessToken: response.authResponse.accessToken }),
        }))
        .then((r: Response) => r.json())
        .then((data: any) => {
          if (!data.success) {
            setFbOAuthStatus('idle');
            const msg = data.friendly || data.error || 'Meta connection failed. Please try again.';
            alert(data.action && data.actionUrl ? `${msg}\n\n${data.action}: ${data.actionUrl}` : msg);
            return;
          }
          setFbOAuthPages(data.pages);
          setFbOAuthStatus('pages');
        })
        .catch(() => {
          setFbOAuthStatus('idle');
          alert('Connection failed. Please check your internet and try again.');
        });
    }, { scope: fbReplyMode === 'comments'
      ? 'pages_show_list,pages_messaging,pages_manage_metadata,pages_manage_engagement,pages_manage_posts'
      : 'pages_show_list,pages_messaging,pages_manage_metadata,pages_manage_posts' });
  };

  const handleSelectFBPage = (page: { id: string; name: string; access_token: string }) => {
    setFbPageId(page.id);
    setFbPageToken(page.access_token);
    setFbOAuthStatus('done');
  };

  const handleMetaConnect = () => {
    const FB = (window as any).FB;
    if (!FB) { alert('Meta SDK not loaded. Please refresh and try again.'); return; }
    setWaOAuthStatus('loading');

    // Embedded Signup (requires META config_id + Meta Business Verification):
    // set REACT_APP_META_CONFIG_ID to enable. Falls back to regular FB.login otherwise.
    const configId = process.env.REACT_APP_META_CONFIG_ID;
    const loginOptions: any = configId
      ? { config_id: configId, response_type: 'code', override_default_response_type: true, extras: { feature: 'whatsapp_embedded_signup' } }
      : { scope: 'business_management,whatsapp_business_management' };

    FB.login((response: any) => {
      if (!response.authResponse) { setWaOAuthStatus('idle'); return; }

      const body: any = configId
        ? { code: response.authResponse.code }
        : { accessToken: response.authResponse.accessToken };

      const apiBase = process.env.NODE_ENV === 'development' ? '' : (process.env.REACT_APP_API_URL || '');
      auth.currentUser!.getIdToken()
        .then((idToken: string) => fetch(`${apiBase}/api/bots/meta-exchange`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
          body: JSON.stringify(body),
        }))
        .then((r: Response) => r.json())
        .then((data: any) => {
          if (!data.success) {
            setWaOAuthStatus('idle');
            const msg = data.friendly || data.error || 'Meta connection failed. Please try again.';
            alert(data.action && data.actionUrl ? `${msg}\n\n${data.action}: ${data.actionUrl}` : msg);
            return;
          }
          setSelfAccessToken(data.longLivedToken);
          setWabaId(data.wabaId);
          setWaNumbers(data.phoneNumbers);
          setWaOAuthStatus('numbers');
        })
        .catch(() => {
          setWaOAuthStatus('idle');
          alert('Connection failed. Please check your internet and try again.');
        });
    }, loginOptions);
  };

  const handleSelectWANumber = (num: { id: string; display_phone_number: string; verified_name: string }) => {
    setSelfPhoneNumberId(num.id);
    setWaOAuthStatus('done');
  };

  const EMAIL_PRESETS: Record<string, { label: string; icon: string; imapHost: string; imapPort: number; imapSecure: boolean; smtpHost: string; smtpPort: number; smtpSecure: boolean; helpText: string }> = {
    gmail:   { label: 'Gmail', icon: '📧', imapHost: 'imap.gmail.com', imapPort: 993, imapSecure: true, smtpHost: 'smtp.gmail.com', smtpPort: 587, smtpSecure: false, helpText: 'Enable 2-Step Verification → Google Account → Security → App Passwords → create one for "Mail". Use that app password here — not your regular Google password.' },
    outlook: { label: 'Outlook / Microsoft 365', icon: '💼', imapHost: 'outlook.office365.com', imapPort: 993, imapSecure: true, smtpHost: 'smtp.office365.com', smtpPort: 587, smtpSecure: false, helpText: 'Use your Microsoft 365 email and password. If MFA is enabled go to account.microsoft.com → Security → Advanced security → App passwords.' },
    yahoo:   { label: 'Yahoo Mail', icon: '🟣', imapHost: 'imap.mail.yahoo.com', imapPort: 993, imapSecure: true, smtpHost: 'smtp.mail.yahoo.com', smtpPort: 587, smtpSecure: false, helpText: 'Generate an App Password: Yahoo Account → Security → App passwords.' },
    cpanel:  { label: 'cPanel / Custom', icon: '⚙️', imapHost: '', imapPort: 993, imapSecure: true, smtpHost: '', smtpPort: 587, smtpSecure: false, helpText: 'Enter your mail server host (e.g. mail.yourdomain.com). Find it in your hosting control panel under Email or Mail Settings.' },
  };

  const applyEmailPreset = (id: string) => {
    const p = EMAIL_PRESETS[id];
    if (!p) return;
    setEmailPreset(id);
    setImapHost(p.imapHost); setImapPort(p.imapPort); setImapSecure(p.imapSecure);
    setSmtpHost(p.smtpHost); setSmtpPort(p.smtpPort); setSmtpSecure(p.smtpSecure);
    setEmailTestStatus('idle'); setEmailTestError('');
  };

  const handleEmailTest = async () => {
    setEmailTestStatus('testing');
    setEmailTestError('');
    try {
      const idToken = await auth.currentUser?.getIdToken();
      const apiBase = process.env.NODE_ENV === 'development' ? '' : (process.env.REACT_APP_API_URL || '');
      const r = await fetch(`${apiBase}/api/email-bot/test-imap`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
        body: JSON.stringify({ imapHost, imapPort, imapSecure, emailUser, emailPass }),
      });
      const data = await r.json();
      if (data.success) { setEmailTestStatus('ok'); }
      else { setEmailTestStatus('error'); setEmailTestError(data.error || 'Connection failed'); }
    } catch {
      setEmailTestStatus('error');
      setEmailTestError('Network error — please try again');
    }
  };

  const userPlan: string = userData?.plan || 'free';
  const userPlanData = plans.find(p => p.id === userPlan);
  const allowedBotTypes = userPlanData?.allowedBotTypes ?? ['website'];
  const canOwnWhatsAppNumber = !!userPlanData?.ownWhatsAppNumber;
  const botLimit = getBotLimit(userPlan);
  const atBotLimit = bots.length >= botLimit;
  const isBotTypeLocked = (t: BotCategory) => !allowedBotTypes.includes(t as string);
  const isAIFlow = botCategory === 'ai';
  const isEmailBot = botCategory === 'email';

  const loadDatasets = async () => {
    if (!auth.currentUser) return;
    setDatasetsLoading(true);
    try {
      const snap = await get(ref(db, `datasets/${auth.currentUser.uid}`));
      const list: Array<{ id: string; name: string; format: string }> = [];
      if (snap.exists()) snap.forEach(c => { list.push({ id: c.key!, name: c.val().name, format: c.val().format }); });
      setDatasets(list);
    } catch (e) { console.error(e); }
    finally { setDatasetsLoading(false); }
  };

  useEffect(() => { if (isAIFlow || isEmailBot) loadDatasets(); }, [isAIFlow, isEmailBot]);
  const needsSetupModeStep = !isAIFlow && !isEmailBot && (botCategory === 'whatsapp' || botCategory === 'facebook');
  const activeMode = botCategory === 'whatsapp' ? whatsappMode : botCategory === 'facebook' ? facebookMode : '';
  const setupFee = (isAIFlow || isEmailBot) ? 0 : getSetupFee(botCategory || '', activeMode || '');
  const setupFeeDesc = getSetupFeeDesc(botCategory, whatsappMode, facebookMode);

  const canProceedStep1 = botCategory !== null && !isBotTypeLocked(botCategory);

  const canProceedStep2 = (() => {
    if (isAIFlow) return aiChannel !== null;
    if (isEmailBot) return emailTestStatus === 'ok';
    if (botCategory === 'whatsapp') {
      if (!whatsappMode) return false;
      if (whatsappMode === 'meta_connect') return waOAuthStatus === 'done';
      return true;
    }
    if (botCategory === 'facebook') {
      if (!facebookMode) return false;
      if (facebookMode === 'oauth') return fbOAuthStatus === 'done';
      return true;
    }
    return true;
  })();

  const canProceedStep3 = (() => {
    if (isAIFlow) {
      if (!businessName.trim()) return false;
      if (aiChannel === 'whatsapp') return selfPhoneNumberId.trim().length > 0 && selfAccessToken.trim().length > 0;
      if (aiChannel === 'facebook') return fbPageId.trim().length > 0 && fbPageToken.trim().length > 0;
      if (aiChannel === 'website') return contactEmail.trim().length > 0;
      return false;
    }
    if (isEmailBot) return businessName.trim().length > 0;
    if (!businessName.trim() || !industry) return false;
    if (botCategory === 'whatsapp') {
      if (whatsappMode === 'meta_connect') return true;
      if (whatsappMode === 'own_number') return ownPhoneNumber.trim().length > 0 && contactEmail.trim().length > 0;
      if (whatsappMode === 'self_setup') return selfPhoneNumberId.trim().length > 0 && selfAccessToken.trim().length > 0;
      if (whatsappMode === 'test_number') return true;
    }
    if (botCategory === 'facebook') {
      if (facebookMode === 'oauth') return true;
      if (facebookMode === 'we_setup') return contactEmail.trim().length > 0;
      if (facebookMode === 'self_setup') return fbPageId.trim().length > 0 && fbPageToken.trim().length > 0;
    }
    if (botCategory === 'website' || botCategory === 'voice') return websiteUrl.trim().length > 0 && contactEmail.trim().length > 0;
    return false;
  })();

  const goNext = (s: number) => {
    if (s === 1) { (isAIFlow || isEmailBot || needsSetupModeStep) ? setStep(2) : setStep(3); return; }
    if (s === 2) { setStep(3); return; }
    if (s === 3) {
      if (isAIFlow || isEmailBot) { createBot(); return; }
      setStep(4); loadFirebaseTemplates(botCategory || 'whatsapp'); return;
    }
  };
  const goBack = (s: number) => {
    if (s === 2) { setStep(1); return; }
    if (s === 3) { (isAIFlow || isEmailBot || needsSetupModeStep) ? setStep(2) : setStep(1); return; }
    if (s === 4) { setStep(3); return; }
  };

  const totalDots = (isAIFlow || isEmailBot) ? 3 : (needsSetupModeStep ? 4 : 3);
  const visualStep = (isAIFlow || isEmailBot) ? step : (needsSetupModeStep ? step : step === 3 ? 2 : step === 4 ? 3 : 1);

  // A Facebook Page / WhatsApp number connected to two bots at once means
  // Meta's webhook can only ever deliver to one of them - the other bot's
  // owner would see zero incoming messages with no indication why. Block
  // that at creation time instead of letting it fail silently later.
  // fbMode, when passed, scopes a Facebook Page conflict check to bots of
  // the SAME kind only - a Page can legitimately carry one messenger-bot and
  // one comments-bot at once (see modules/facebook.js's matching dedup fix
  // in the webhook handler), so an existing bot of the *other* kind on that
  // Page is never a real conflict.
  const findConnectionConflict = async (field: 'fbPageId' | 'phoneNumberId', value: string, fbMode?: 'messenger' | 'comments'): Promise<{ id: string; businessName?: string } | null> => {
    if (!value) return null;
    const q = query(ref(db, 'bots'), orderByChild(field), equalTo(value));
    const snap = await get(q);
    const val = snap.val();
    if (!val) return null;
    for (const [id, b] of Object.entries<any>(val)) {
      if (b?.status === 'deleted') continue;
      if (fbMode && (b?.fbMode === 'comments' ? 'comments' : 'messenger') !== fbMode) continue;
      return { id, businessName: b?.businessName };
    }
    return null;
  };

  const createBot = async () => {
    if (!auth.currentUser) { alert('Please log in first'); return; }
    if (atBotLimit) { navigate('/billing'); return; }
    if (botCategory && isBotTypeLocked(botCategory)) { navigate('/billing'); return; }
    if (!businessName.trim()) { alert('Please enter a business name'); return; }

    setLoading(true);
    try {
      const uid = auth.currentUser.uid;
      const botRef = push(ref(db, 'bots'));
      const botId = botRef.key!;

      // Resolve triggers — Firebase template or built-in
      const fbTpl = firebaseTemplates.find(t => t.id === selectedTemplate);
      let triggers: any[];
      if (fbTpl) {
        triggers = (fbTpl.triggers || []).map((t: any) => {
          const c = JSON.parse(JSON.stringify(t));
          if (c.trigger === 'hi' && c.responses[0]?.content)
            c.responses[0].content = c.responses[0].content.replace(/Greenfield Academy/g, businessName);
          return c;
        });
      } else {
        const tpl = INDUSTRY_TEMPLATES[selectedTemplate] || INDUSTRY_TEMPLATES.custom;
        triggers = tpl.triggers.map((t: any) => {
          const c = JSON.parse(JSON.stringify(t));
          if (c.trigger === 'hi' && c.responses[0]?.content) c.responses[0].content = `Welcome to ${businessName}! ` + c.responses[0].content;
          return c;
        });
      }

      // ── Email Bot — IMAP/SMTP credentials + AI config ────────────────────
      if (isEmailBot) {
        const botData: any = {
          businessName: businessName.trim(),
          userId: uid,
          welcomeMessage: welcomeMessage || `Hi! I'm ${businessName}'s email assistant.`,
          createdAt: Date.now(),
          status: 'active',
          botType: 'email',
          emailUser: emailUser.trim(),
          emailPass: emailPass,
          emailFrom: emailFrom.trim() || `${businessName.trim()} <${emailUser.trim()}>`,
          imapHost: imapHost.trim(),
          imapPort: Number(imapPort) || 993,
          imapSecure: !!imapSecure,
          smtpHost: smtpHost.trim(),
          smtpPort: Number(smtpPort) || 587,
          smtpSecure: !!smtpSecure,
          aiConfig: { model: 'openai/gpt-oss-20b', ...(selectedDatasetId ? { datasetId: selectedDatasetId } : {}) },
          setupFeeAmount: 0,
          setupFeePaid: true,
        };
        await set(botRef, botData);
        apiFetch('/api/notify/bot-created', {
          method: 'POST',
          body: JSON.stringify({ botId, botName: businessName.trim(), botType: 'email', setupFee: 0 }),
        }).catch(() => {});
        navigate('/dashboard');
        return;
      }

      // accessToken is never written to bots/{id} directly - it's a real
      // WhatsApp/Facebook credential and that node is broadly readable by
      // any logged-in user (dashboard "my bots" list query needs it to be).
      // Captured here and written separately to botSecrets/{id} right after
      // bot creation, which is properly owner-scoped.
      let pendingAccessToken: string | null = null;

      // ── AI Bot — skip flow/template, store channel + AI config ───────────
      if (isAIFlow) {
        const botData: any = {
          businessName: businessName.trim(),
          userId: uid,
          welcomeMessage: welcomeMessage || `Hi! I'm ${businessName}'s AI assistant. How can I help?`,
          createdAt: Date.now(),
          status: 'active',
          botType: 'ai',
          aiChannel,
          contactEmail: contactEmail.trim(),
          aiConfig: { model: 'openai/gpt-oss-20b', ...(selectedDatasetId ? { datasetId: selectedDatasetId } : {}) },
          setupFeeAmount: 0,
          setupFeePaid: true,
        };
        if (aiChannel === 'whatsapp') {
          botData.phoneNumberId = selfPhoneNumberId.trim();
          pendingAccessToken = selfAccessToken.trim();
        } else if (aiChannel === 'facebook') {
          botData.fbPageId = fbPageId.trim();
          pendingAccessToken = fbPageToken.trim();
        } else if (aiChannel === 'website') {
          botData.websiteUrl = websiteUrl.trim();
        }

        if (aiChannel === 'whatsapp' && botData.phoneNumberId) {
          const conflict = await findConnectionConflict('phoneNumberId', botData.phoneNumberId);
          if (conflict) {
            alert(`This WhatsApp number is already connected to another bot${conflict.businessName ? ` ("${conflict.businessName}")` : ''}. Messages would only ever reach one of them — disconnect that bot's number first, or use a different number.`);
            return;
          }
        } else if (aiChannel === 'facebook' && botData.fbPageId) {
          const conflict = await findConnectionConflict('fbPageId', botData.fbPageId, 'messenger');
          if (conflict) {
            alert(`This Facebook Page is already connected to another messaging bot${conflict.businessName ? ` ("${conflict.businessName}")` : ''}. Messages would only ever reach one of them — disconnect that bot's Page first, or connect a different Page.`);
            return;
          }
        }

        await set(botRef, botData);
        if (pendingAccessToken) await set(ref(db, `botSecrets/${botId}`), { accessToken: pendingAccessToken });
        apiFetch('/api/notify/bot-created', {
          method: 'POST',
          body: JSON.stringify({ botId, botName: businessName.trim(), botType: 'ai', aiChannel, setupFee: 0 }),
        }).catch(() => {});
        navigate('/dashboard');
        return;
      }

      const botData: any = {
        businessName: businessName.trim(),
        userId: uid,
        welcomeMessage: welcomeMessage || `Welcome to ${businessName}! How can I help you today?`,
        createdAt: Date.now(),
        status: 'active',
        industry: industry || selectedTemplate,
        template: selectedTemplate,
        botType: botCategory,
        contactEmail: contactEmail.trim(),
        setupFeeAmount: setupFee,
        setupFeePaid: setupFee === 0,
      };

      if (botCategory === 'whatsapp') {
        botData.whatsappMode = whatsappMode;
        if (whatsappMode === 'meta_connect') {
          botData.phoneNumberId = selfPhoneNumberId.trim();
          pendingAccessToken = selfAccessToken.trim();
          botData.wabaId = wabaId.trim();
          botData.status = 'active';
        } else if (whatsappMode === 'own_number') {
          botData.phoneNumber = ownPhoneNumber.trim();
          botData.country = ownPhoneCountry;
          botData.businessAddress = businessAddress.trim();
          botData.websiteUrl = websiteUrl.trim();
          botData.status = 'pending_setup';
        } else if (whatsappMode === 'self_setup') {
          botData.phoneNumberId = selfPhoneNumberId.trim();
          pendingAccessToken = selfAccessToken.trim();
          botData.status = 'active';
        } else if (whatsappMode === 'test_number') {
          const apiBase = process.env.NODE_ENV === 'development' ? '' : (process.env.REACT_APP_API_URL || '');
          const kwRes = await fetch(`${apiBase}/api/test-number/info?name=${encodeURIComponent(businessName.trim())}`);
          const kwData = await kwRes.json();
          const kw = kwData.keyword || businessName.trim().toLowerCase().replace(/[^a-z0-9]/g, '').substring(0, 10) || 'bot';
          setTestKeyword(kw);
          botData.isTestNumber = true;
          botData.testKeyword = kw;
          botData.testDisplayNumber = kwData.displayNumber || null;
          botData.trialStartedAt = Date.now();
          botData.trialDays = 7;
          botData.status = 'active';
        }
      } else if (botCategory === 'facebook') {
        botData.facebookMode = facebookMode;
        botData.fbMode = fbReplyMode === 'comments' ? 'comments' : 'messenger';
        if (facebookMode === 'oauth' || facebookMode === 'self_setup') {
          botData.fbPageId = fbPageId.trim();
          pendingAccessToken = fbPageToken.trim();
          botData.status = 'active';
        } else if (facebookMode === 'we_setup') {
          botData.fbPageUrl = fbPageUrl.trim();
          botData.status = 'pending_setup';
        }
      } else if (botCategory === 'website') {
        botData.websiteUrl = websiteUrl.trim();
      } else if (botCategory === 'voice') {
        botData.websiteUrl = websiteUrl.trim();
        botData.status = 'pending_setup';
      }

      if (botCategory === 'whatsapp' && botData.phoneNumberId) {
        const conflict = await findConnectionConflict('phoneNumberId', botData.phoneNumberId);
        if (conflict) {
          alert(`This WhatsApp number is already connected to another bot${conflict.businessName ? ` ("${conflict.businessName}")` : ''}. Messages would only ever reach one of them — disconnect that bot's number first, or use a different number.`);
          return;
        }
      } else if (botCategory === 'facebook' && botData.fbPageId) {
        const conflict = await findConnectionConflict('fbPageId', botData.fbPageId, botData.fbMode);
        if (conflict) {
          const kind = botData.fbMode === 'comments' ? 'comment-reply' : 'messaging';
          alert(`This Facebook Page is already connected to another ${kind} bot${conflict.businessName ? ` ("${conflict.businessName}")` : ''}. ${botData.fbMode === 'comments' ? 'Comments' : 'Messages'} would only ever reach one of them — disconnect that bot's Page first, or connect a different Page.`);
          return;
        }
      }

      await set(botRef, botData);
      if (pendingAccessToken) await set(ref(db, `botSecrets/${botId}`), { accessToken: pendingAccessToken });
      await set(ref(db, `flows/${botId}`), { name: businessName, botId, triggers, userId: uid, createdAt: Date.now(), updatedAt: Date.now() });

      // Self-setup bots save Meta credentials straight from this client with
      // no backend involvement — nothing else ever calls /subscribed_apps for
      // them, so Meta would otherwise never deliver a single webhook event.
      if ((botCategory === 'whatsapp' && whatsappMode === 'self_setup') || (botCategory === 'facebook' && facebookMode === 'self_setup')) {
        apiFetch(`/api/bots/${botId}/self-setup-subscribe`, { method: 'POST' })
          .then(r => r.json())
          .then(d => { if (!d.success) console.warn('Webhook subscription failed:', d.friendly || d.error); })
          .catch(() => {});
      }

      if (setupFee > 0) {
        const invoiceId = await createSetupInvoice(uid, businessName.trim(), botId, setupFee, setupFeeDesc, contactEmail.trim());
        await set(ref(db, `bots/${botId}/pendingInvoiceId`), invoiceId);
        await set(ref(db, `bots/${botId}/status`), 'pending_payment');

        apiFetch('/api/notify/bot-created', {
          method: 'POST',
          body: JSON.stringify({ botId, botName: businessName.trim(), botType: botCategory, setupFee, invoiceId }),
        }).catch(() => {});

        navigate(`/billing?invoiceId=${invoiceId}`);
        return;
      }

      apiFetch('/api/notify/bot-created', {
        method: 'POST',
        body: JSON.stringify({ botId, botName: businessName.trim(), botType: botCategory, setupFee: 0 }),
      }).catch(() => {});

      navigate('/dashboard');
    } catch (e) {
      console.error(e);
      alert('Error creating bot: ' + e);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => { await signOut(auth); navigate('/'); };

  return (
    <>
      <SEO title="Create Bot | autopilot" description="Create a WhatsApp, Facebook, Website, or Voice bot for your business." keywords="create bot, WhatsApp bot, Facebook bot, website chatbot" type="website" noindex />
      <div style={S.page}>

        {/* Sidebar */}
        <div style={S.sidebar(sidebarCollapsed)}>
          <button onClick={() => setSidebarCollapsed(!sidebarCollapsed)} style={{ position: 'absolute', right: '-13px', top: '22px', width: '26px', height: '26px', background: '#5B4FE8', border: 'none', borderRadius: '50%', color: 'white', cursor: 'pointer', zIndex: 10, fontSize: '11px' }}>
            {sidebarCollapsed ? '→' : '←'}
          </button>
          <div style={{ padding: sidebarCollapsed ? '20px 0' : '22px 18px', borderBottom: '1px solid var(--border-subtle)' }}>
            {!sidebarCollapsed ? (<><div style={{ color: 'var(--text-primary)', fontWeight: '700', fontSize: '18px' }}>ZimChat</div><div style={{ color: 'var(--text-faint)', fontSize: '11px', marginTop: '2px' }}>Bot Platform</div></>) : <div style={{ textAlign: 'center', fontSize: '20px' }}>🤖</div>}
          </div>
          <nav style={{ flex: 1, padding: sidebarCollapsed ? '14px 0' : '16px 10px', overflowY: 'auto' }}>
            {[{ to: '/dashboard', icon: '📊', label: 'Dashboard' }, { to: '/billing', icon: '💳', label: 'Billing' }].map(({ to, icon, label }) => (
              <Link key={to} to={to} style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: sidebarCollapsed ? '10px 0' : '9px 12px', borderRadius: '9px', color: 'var(--text-muted)', marginBottom: '3px', justifyContent: sidebarCollapsed ? 'center' : 'flex-start' }}>
                  <span style={{ fontSize: '16px' }}>{icon}</span>
                  {!sidebarCollapsed && <span style={{ fontSize: '13px', fontWeight: '500' }}>{label}</span>}
                </div>
              </Link>
            ))}
            <div onClick={handleLogout} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: sidebarCollapsed ? '10px 0' : '9px 12px', borderRadius: '9px', color: 'var(--text-muted)', cursor: 'pointer', justifyContent: sidebarCollapsed ? 'center' : 'flex-start' }}>
              <span style={{ fontSize: '16px' }}>🚪</span>
              {!sidebarCollapsed && <span style={{ fontSize: '13px', fontWeight: '500' }}>Logout</span>}
            </div>
          </nav>
          {!sidebarCollapsed && user && <div style={{ padding: '12px 18px', borderTop: '1px solid var(--border-subtle)', fontSize: '11px', color: 'var(--text-faint)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.email}</div>}
        </div>

        {/* Main */}
        <div style={S.main}>
          <div style={{ maxWidth: '680px', margin: '0 auto' }}>

            <div style={{ marginBottom: '36px' }}>
              <div style={{ color: '#5B4FE8', fontSize: '11px', fontWeight: '700', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '6px' }}>New Bot</div>
              <h1 style={{ color: 'var(--text-primary)', fontSize: '28px', fontWeight: '700', letterSpacing: '-0.02em', margin: 0 }}>Create your bot</h1>
              <p style={{ color: 'var(--text-faint)', fontSize: '14px', marginTop: '6px' }}>A few quick steps and your bot will be ready to chat with customers</p>
            </div>

            {atBotLimit && (
              <div style={{ background: '#f59e0b0d', border: '1px solid #f59e0b30', borderLeft: '3px solid #f59e0b', borderRadius: '10px', padding: '14px 18px', marginBottom: '28px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px' }}>
                <div>
                  <div style={{ color: '#f59e0b', fontWeight: '600', fontSize: '14px', marginBottom: '2px' }}>Bot limit reached</div>
                  <div style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>Your <strong style={{ color: 'var(--text-primary)' }}>{userPlan}</strong> plan allows {botLimit} bot{botLimit > 1 ? 's' : ''}.</div>
                </div>
                <button onClick={() => navigate('/billing')} style={{ padding: '8px 18px', background: '#f59e0b', color: '#1a1200', border: 'none', borderRadius: '8px', fontWeight: '700', fontSize: '13px', cursor: 'pointer', whiteSpace: 'nowrap' }}>Upgrade</button>
              </div>
            )}

            <StepDots total={totalDots} current={visualStep} />

            {/* STEP 1: Channel */}
            {step === 1 && (
              <div>
                <div style={{ color: 'var(--text-primary)', fontSize: '18px', fontWeight: '600', marginBottom: '4px' }}>Where do your customers chat with you?</div>
                <div style={{ color: 'var(--text-faint)', fontSize: '13px', marginBottom: '24px' }}>Pick the channel they use most</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '20px' }}>
                  <BotTypeCard icon="💬" label="WhatsApp" badge="POPULAR" badgeColor="#10b981" description="Chat with customers directly in WhatsApp. Great for bookings, FAQs, and sales." selected={botCategory === 'whatsapp'} locked={isBotTypeLocked('whatsapp')} lockReason="Requires a paid plan" onClick={() => { setBotCategory('whatsapp'); setWhatsappMode(null); }} />
                  <BotTypeCard icon="📘" label="Facebook Messenger" description="Auto-reply to messages on your Facebook business page, day and night." selected={botCategory === 'facebook'} locked={isBotTypeLocked('facebook')} lockReason="Requires a paid plan" onClick={() => { setBotCategory('facebook'); setFacebookMode(null); }} />
                  <BotTypeCard icon="🌐" label="Website Chat" badge="FREE" badgeColor="#10b981" description="Add a chat bubble to your website. Visitors get instant answers." selected={botCategory === 'website'} onClick={() => setBotCategory('website')} />
                  <BotTypeCard icon="🎙️" label="Voice Bot" badge="BETA" badgeColor="#f59e0b" description="An AI voice assistant on your website — answers questions hands-free." selected={botCategory === 'voice'} locked={isBotTypeLocked('voice')} lockReason="Requires a paid plan" onClick={() => { setBotCategory('voice'); }} />
                  <BotTypeCard icon="🤖" label="AI Bot" badge="BUSINESS+" badgeColor="#a855f7" description="Powered by AI. Answers everything automatically — no triggers needed. Choose WhatsApp, Website, or Facebook." selected={botCategory === 'ai'} locked={isBotTypeLocked('ai')} lockReason="Requires Business plan or above" onClick={() => { setBotCategory('ai'); setAIChannel(null); }} />
                  <BotTypeCard icon="📧" label="Email Bot" badge="STARTER+" badgeColor="#10b981" description="Auto-reply to emails via IMAP. Connects to Gmail, Outlook/M365, Yahoo, or any cPanel inbox." selected={botCategory === 'email'} locked={isBotTypeLocked('email')} lockReason="Requires a paid plan" onClick={() => { setBotCategory('email'); setEmailPreset(''); setEmailTestStatus('idle'); }} />
                </div>
                {botCategory === 'facebook' && (
                  <div style={{ marginBottom: '20px' }}>
                    <div style={{ color: 'var(--text-faint)', fontSize: '12px', marginBottom: '8px' }}>What should it do on your Page?</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                      <SetupOptionCard icon="💬" title="Reply to messages" description="Auto-reply to private messages people send your Page, same as Facebook Messenger always has." selected={fbReplyMode === 'messages'} onClick={() => setFbReplyMode('messages')} />
                      <SetupOptionCard icon="📝" title="Reply to comments" description="Auto-reply to comments people leave on your Page's posts, publicly, powered by AI." badge="NEW" badgeColor="#a855f7" selected={fbReplyMode === 'comments'} onClick={() => setFbReplyMode('comments')} />
                    </div>
                  </div>
                )}
                {!allowedBotTypes.includes('whatsapp') && (
                  <div style={{ background: '#5B4FE80d', border: '1px solid #5B4FE830', borderRadius: '10px', padding: '14px 16px', marginBottom: '20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px' }}>
                    <span style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>🔒 <strong style={{ color: '#818cf8' }}>WhatsApp, Facebook & Voice</strong> bots require a paid plan. Free plan includes 1 website chatbot.</span>
                    <button onClick={() => navigate('/billing')} style={{ padding: '7px 16px', background: 'linear-gradient(135deg,#5B4FE8,#7c3aed)', color: 'white', border: 'none', borderRadius: '8px', fontWeight: '600', fontSize: '12px', cursor: 'pointer', whiteSpace: 'nowrap' }}>Upgrade →</button>
                  </div>
                )}
                {!allowedBotTypes.includes('ai') && (
                  <div style={{ background: '#a855f70d', border: '1px solid #a855f730', borderRadius: '10px', padding: '12px 16px', marginBottom: '8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px' }}>
                    <span style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>🤖 <strong style={{ color: '#c084fc' }}>AI Bot</strong> requires a Business plan or above.</span>
                    <button onClick={() => navigate('/billing')} style={{ padding: '7px 16px', background: 'linear-gradient(135deg,#a855f7,#7c3aed)', color: 'white', border: 'none', borderRadius: '8px', fontWeight: '600', fontSize: '12px', cursor: 'pointer', whiteSpace: 'nowrap' }}>Upgrade →</button>
                  </div>
                )}
                {!allowedBotTypes.includes('email') && (
                  <div style={{ background: '#10b9810d', border: '1px solid #10b98130', borderRadius: '10px', padding: '12px 16px', marginBottom: '8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px' }}>
                    <span style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>📧 <strong style={{ color: '#34d399' }}>Email Bot</strong> requires a Starter plan or above.</span>
                    <button onClick={() => navigate('/billing')} style={{ padding: '7px 16px', background: 'linear-gradient(135deg,#10b981,#059669)', color: 'white', border: 'none', borderRadius: '8px', fontWeight: '600', fontSize: '12px', cursor: 'pointer', whiteSpace: 'nowrap' }}>Upgrade →</button>
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <button onClick={() => atBotLimit ? navigate('/billing') : goNext(1)} disabled={!canProceedStep1 || atBotLimit} style={S.btnPrimary(!canProceedStep1 || atBotLimit)}>
                    {atBotLimit ? 'Upgrade to continue' : 'Continue →'}
                  </button>
                </div>
              </div>
            )}

            {/* STEP 2: AI — pick channel */}
            {step === 2 && isAIFlow && (
              <div>
                <div style={{ color: 'var(--text-primary)', fontSize: '18px', fontWeight: '600', marginBottom: '4px' }}>Where should your AI bot live?</div>
                <div style={{ color: 'var(--text-faint)', fontSize: '13px', marginBottom: '24px' }}>Choose the channel your customers use — you can create separate AI bots for each</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '24px' }}>
                  <SetupOptionCard icon="💬" title="WhatsApp" description="Your AI bot replies to every WhatsApp message automatically. You'll need your WhatsApp Business API credentials (Phone Number ID + Access Token)." badge="POPULAR" badgeColor="#10b981" selected={aiChannel === 'whatsapp'} onClick={() => setAIChannel('whatsapp')} />
                  <SetupOptionCard icon="🌐" title="Website Chat" description="Add a smart AI chat widget to your website. The AI answers visitor questions in real time. No additional credentials needed beyond your AI API key." selected={aiChannel === 'website'} onClick={() => setAIChannel('website')} />
                  <SetupOptionCard icon="📘" title="Facebook Messenger" description="Your AI bot auto-responds to every Messenger message on your Facebook Page. You'll need your Page ID and Page Access Token." selected={aiChannel === 'facebook'} onClick={() => setAIChannel('facebook')} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <button onClick={() => goBack(2)} style={S.btnGhost}>← Back</button>
                  <button onClick={() => goNext(2)} disabled={!canProceedStep2} style={S.btnPrimary(!canProceedStep2)}>Continue →</button>
                </div>
              </div>
            )}

            {/* STEP 2: Setup mode – WhatsApp */}
            {step === 2 && botCategory === 'whatsapp' && (
              <div>
                <div style={{ color: 'var(--text-primary)', fontSize: '18px', fontWeight: '600', marginBottom: '4px' }}>How would you like to set up your WhatsApp?</div>
                <div style={{ color: 'var(--text-faint)', fontSize: '13px', marginBottom: '24px' }}>Choose the option that works best for you</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '24px' }}>
                  <SetupOptionCard icon="🔗" title="Connect Meta Business" description="Sign in with your Meta account and we automatically detect your WhatsApp Business Account and phone number. Fastest path to live — no copy-pasting credentials." badge={canOwnWhatsAppNumber ? "RECOMMENDED" : undefined} badgeColor="#10b981" selected={whatsappMode === 'meta_connect'} locked={!canOwnWhatsAppNumber} lockReason="Requires Business plan+" onClick={() => { if (!canOwnWhatsAppNumber) return; setWhatsappMode('meta_connect'); setWaOAuthStatus('idle'); }} />
                  <SetupOptionCard icon="📱" title="We register your number" description="Provide your existing business phone number and we handle the Meta registration for you. Approval takes 1–3 business days." fee="$20 one-time setup fee" feeColor="#f59e0b" selected={whatsappMode === 'own_number'} locked={!canOwnWhatsAppNumber} lockReason="Requires Business plan+" onClick={() => canOwnWhatsAppNumber && setWhatsappMode('own_number')} />
                  <SetupOptionCard icon="⚙️" title="I already have API access" description="Already have a WhatsApp Business API account? Paste your Phone Number ID and Access Token from Meta Developer Console and you're live instantly." badge="ADVANCED" badgeColor="var(--text-muted)" selected={whatsappMode === 'self_setup'} locked={!canOwnWhatsAppNumber} lockReason="Requires Business plan+" onClick={() => canOwnWhatsAppNumber && setWhatsappMode('self_setup')} />
                  <SetupOptionCard icon="🧪" title="Use Autopilot Test Number" description="Test your bot instantly using our shared WhatsApp number. No credentials needed — just build your flow and start a 7-day free trial." badge="FREE TRIAL" badgeColor="#5B4FE8" selected={whatsappMode === 'test_number'} onClick={() => setWhatsappMode('test_number')} />
                </div>

                {whatsappMode === 'test_number' && (
                  <div style={{ ...S.card, marginBottom: '20px' }}>
                    {!testNumberInfo ? (
                      <div style={{ color: 'var(--text-muted)', fontSize: '13px', textAlign: 'center', padding: '8px 0' }}>Checking availability…</div>
                    ) : !testNumberInfo.configured ? (
                      <div style={{ color: '#f59e0b', fontSize: '13px' }}>⚠️ The shared test number is not yet configured. Please contact support to enable it.</div>
                    ) : (
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '14px' }}>
                          <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: '#5B4FE820', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <span style={{ fontSize: '18px' }}>🧪</span>
                          </div>
                          <div>
                            <div style={{ color: 'var(--text-primary)', fontWeight: '600', fontSize: '14px' }}>Test number available</div>
                            <div style={{ color: 'var(--text-muted)', fontSize: '12px' }}>{testNumberInfo.displayNumber || 'Shared number active'}</div>
                          </div>
                        </div>
                        <div style={{ background: 'var(--bg-page)', borderRadius: '10px', padding: '14px 16px', fontSize: '12px', color: 'var(--text-secondary)', lineHeight: '1.8' }}>
                          <div>✅ <strong>7-day free trial</strong> — no setup fee, no credentials needed</div>
                          <div>📲 Your customers use a unique keyword to activate your bot on the shared number</div>
                          <div>🔒 After 7 days the bot is locked — upgrade to connect your own dedicated number</div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {whatsappMode === 'meta_connect' && (
                  <div style={{ ...S.card, marginBottom: '20px' }}>
                    {waOAuthStatus === 'idle' && (
                      <div style={{ textAlign: 'center', padding: '8px 0' }}>
                        <div style={{ color: 'var(--text-secondary)', fontSize: '13px', marginBottom: '16px' }}>Click below to connect your Meta Business account. We'll automatically find your WhatsApp Business number.</div>
                        <button onClick={handleMetaConnect}
                          style={{ background: 'linear-gradient(135deg,#0082fb,#0064d9)', color: '#fff', border: 'none', borderRadius: '8px', padding: '12px 24px', fontSize: '14px', fontWeight: '700', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ fontSize: '18px' }}>🔗</span> Connect Meta Business
                        </button>
                      </div>
                    )}
                    {waOAuthStatus === 'loading' && (
                      <div style={{ color: 'var(--text-secondary)', textAlign: 'center', padding: '8px 0' }}>Connecting to Meta…</div>
                    )}
                    {waOAuthStatus === 'numbers' && (
                      <div>
                        <div style={{ color: 'var(--text-primary)', fontWeight: '600', fontSize: '13px', marginBottom: '12px' }}>Select your WhatsApp Business number:</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          {waNumbers.map(n => (
                            <div key={n.id} onClick={() => handleSelectWANumber(n)}
                              style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 16px', borderRadius: '9px', cursor: 'pointer', border: '1px solid var(--bg-element)', background: 'var(--bg-page)', transition: 'all 0.15s' }}
                              onMouseEnter={e => (e.currentTarget.style.borderColor = '#5B4FE8')}
                              onMouseLeave={e => (e.currentTarget.style.borderColor = 'var(--bg-element)')}>
                              <span style={{ fontSize: '22px' }}>📱</span>
                              <div>
                                <div style={{ color: 'var(--text-primary)', fontWeight: '600', fontSize: '14px' }}>{n.display_phone_number}</div>
                                <div style={{ color: 'var(--text-faint)', fontSize: '11px' }}>{n.verified_name} · ID: {n.id}</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {waOAuthStatus === 'done' && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '4px 0' }}>
                        <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: '#10b98120', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <span style={{ color: '#10b981', fontSize: '18px' }}>✓</span>
                        </div>
                        <div>
                          <div style={{ color: '#10b981', fontWeight: '600', fontSize: '14px' }}>Number connected!</div>
                          <div style={{ color: 'var(--text-muted)', fontSize: '12px' }}>
                            {waNumbers.find(n => n.id === selfPhoneNumberId)?.display_phone_number || selfPhoneNumberId}
                          </div>
                        </div>
                        <button onClick={() => { setWaOAuthStatus('idle'); setSelfPhoneNumberId(''); setSelfAccessToken(''); }}
                          style={{ marginLeft: 'auto', background: 'transparent', border: '1px solid var(--bg-element)', color: 'var(--text-muted)', borderRadius: '6px', padding: '4px 10px', fontSize: '12px', cursor: 'pointer' }}>
                          Change
                        </button>
                      </div>
                    )}
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <button onClick={() => goBack(2)} style={S.btnGhost}>← Back</button>
                  <button onClick={() => goNext(2)} disabled={!canProceedStep2} style={S.btnPrimary(!canProceedStep2)}>Continue →</button>
                </div>
              </div>
            )}

            {/* STEP 2: Setup mode – Facebook */}
            {step === 2 && botCategory === 'facebook' && (
              <div>
                <div style={{ color: 'var(--text-primary)', fontSize: '18px', fontWeight: '600', marginBottom: '4px' }}>How would you like to set up your Facebook bot?</div>
                <div style={{ color: 'var(--text-faint)', fontSize: '13px', marginBottom: '24px' }}>Choose the option that works best for you</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '24px' }}>
                  <SetupOptionCard icon="📘" title="Connect via Facebook" description="Sign in with your Facebook account and we'll automatically find your Pages and link them to the bot. No technical knowledge needed — go live instantly." badge="RECOMMENDED" badgeColor="#10b981" selected={facebookMode === 'oauth'} onClick={() => { setFacebookMode('oauth'); setFbOAuthStatus('idle'); }} />
                  <SetupOptionCard icon="🛠️" title="We set it up for you" description="Our team connects your Facebook Page to the bot. Just provide your page name or URL and we'll handle everything. Setup takes 1–2 business days." fee="$50 one-time setup fee" feeColor="#f59e0b" selected={facebookMode === 'we_setup'} onClick={() => setFacebookMode('we_setup')} />
                  <SetupOptionCard icon="⚙️" title="I have my credentials" description="Already have a Meta Developer account? Paste your Facebook Page ID and Page Access Token to go live immediately." badge="ADVANCED" badgeColor="var(--text-muted)" selected={facebookMode === 'self_setup'} onClick={() => setFacebookMode('self_setup')} />
                </div>

                {facebookMode === 'oauth' && (
                  <div style={{ ...S.card, marginBottom: '20px' }}>
                    {fbOAuthStatus === 'idle' && (
                      <div style={{ textAlign: 'center', padding: '8px 0' }}>
                        <div style={{ color: 'var(--text-secondary)', fontSize: '13px', marginBottom: '16px' }}>Click below to sign in with Facebook and select the Page you want to connect.</div>
                        <button onClick={handleFacebookOAuth}
                          style={{ background: '#1877F2', color: '#fff', border: 'none', borderRadius: '8px', padding: '12px 24px', fontSize: '14px', fontWeight: '700', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ fontSize: '18px' }}>📘</span> Continue with Facebook
                        </button>
                      </div>
                    )}
                    {fbOAuthStatus === 'loading' && (
                      <div style={{ color: 'var(--text-secondary)', textAlign: 'center', padding: '8px 0' }}>Connecting to Facebook…</div>
                    )}
                    {fbOAuthStatus === 'pages' && (
                      <div>
                        <div style={{ color: 'var(--text-primary)', fontWeight: '600', fontSize: '13px', marginBottom: '12px' }}>Select the Facebook Page to connect:</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          {fbOAuthPages.map(p => (
                            <div key={p.id} onClick={() => handleSelectFBPage(p)}
                              style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 16px', borderRadius: '9px', cursor: 'pointer', border: '1px solid var(--bg-element)', background: 'var(--bg-page)', transition: 'all 0.15s' }}
                              onMouseEnter={e => (e.currentTarget.style.borderColor = '#5B4FE8')}
                              onMouseLeave={e => (e.currentTarget.style.borderColor = 'var(--bg-element)')}>
                              <span style={{ fontSize: '22px' }}>📄</span>
                              <div>
                                <div style={{ color: 'var(--text-primary)', fontWeight: '600', fontSize: '14px' }}>{p.name}</div>
                                <div style={{ color: 'var(--text-faint)', fontSize: '11px' }}>{p.category} · ID: {p.id}</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {fbOAuthStatus === 'done' && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '4px 0' }}>
                        <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: '#10b98120', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <span style={{ color: '#10b981', fontSize: '18px' }}>✓</span>
                        </div>
                        <div>
                          <div style={{ color: '#10b981', fontWeight: '600', fontSize: '14px' }}>Page connected!</div>
                          <div style={{ color: 'var(--text-muted)', fontSize: '12px' }}>
                            {fbOAuthPages.find(p => p.id === fbPageId)?.name || fbPageId}
                          </div>
                        </div>
                        <button onClick={() => { setFbOAuthStatus('idle'); setFbPageId(''); setFbPageToken(''); }}
                          style={{ marginLeft: 'auto', background: 'transparent', border: '1px solid var(--bg-element)', color: 'var(--text-muted)', borderRadius: '6px', padding: '4px 10px', fontSize: '12px', cursor: 'pointer' }}>
                          Change
                        </button>
                      </div>
                    )}
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <button onClick={() => goBack(2)} style={S.btnGhost}>← Back</button>
                  <button onClick={() => goNext(2)} disabled={!canProceedStep2} style={S.btnPrimary(!canProceedStep2)}>Continue →</button>
                </div>
              </div>
            )}

            {/* STEP 2: Email bot — credentials */}
            {step === 2 && isEmailBot && (
              <div>
                <div style={{ color: 'var(--text-primary)', fontSize: '18px', fontWeight: '600', marginBottom: '4px' }}>Connect your email account</div>
                <div style={{ color: 'var(--text-faint)', fontSize: '13px', marginBottom: '24px' }}>Choose your email provider and enter your credentials. The bot will poll your inbox every 3 minutes for new messages.</div>

                {/* Provider presets */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px', marginBottom: '20px' }}>
                  {Object.entries(EMAIL_PRESETS).map(([id, p]) => (
                    <div key={id} onClick={() => applyEmailPreset(id)}
                      style={{ padding: '14px 16px', borderRadius: '11px', cursor: 'pointer', border: emailPreset === id ? '2px solid #5B4FE8' : '1px solid var(--bg-element)', background: emailPreset === id ? 'rgba(91,79,232,0.08)' : 'var(--bg-card)', transition: 'all 0.15s', display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <span style={{ fontSize: '22px' }}>{p.icon}</span>
                      <span style={{ color: emailPreset === id ? '#818cf8' : 'var(--text-primary)', fontWeight: '600', fontSize: '13px' }}>{p.label}</span>
                      {emailPreset === id && <span style={{ marginLeft: 'auto', color: '#5B4FE8', fontSize: '14px' }}>✓</span>}
                    </div>
                  ))}
                </div>

                {emailPreset && (
                  <div style={S.card}>
                    {EMAIL_PRESETS[emailPreset]?.helpText && (
                      <InfoBox items={[EMAIL_PRESETS[emailPreset].helpText]} color="#5B4FE8" />
                    )}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '14px' }}>
                      <div>
                        <label style={S.label}>Email Address <span style={{ color: '#ef4444' }}>*</span></label>
                        <input type="email" value={emailUser} onChange={e => { setEmailUser(e.target.value); setEmailTestStatus('idle'); }} placeholder="you@yourdomain.com" style={S.input} />
                      </div>
                      <div>
                        <label style={S.label}>Password / App Password <span style={{ color: '#ef4444' }}>*</span></label>
                        <input type="password" value={emailPass} onChange={e => { setEmailPass(e.target.value); setEmailTestStatus('idle'); }} placeholder="app password" style={S.input} />
                      </div>
                    </div>
                    <div style={{ marginBottom: '14px' }}>
                      <label style={S.label}>Display Name (From)</label>
                      <input type="text" value={emailFrom} onChange={e => setEmailFrom(e.target.value)} placeholder={`e.g. Acme Support <you@yourdomain.com>`} style={S.input} />
                      <div style={{ color: 'var(--text-faint)', fontSize: '11px', marginTop: '4px' }}>How your replies appear to customers. Defaults to bot name + email if blank.</div>
                    </div>

                    {emailPreset === 'cpanel' && (
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px 1fr 80px', gap: '10px', marginBottom: '14px', alignItems: 'end' }}>
                        <div>
                          <label style={S.label}>IMAP Host <span style={{ color: '#ef4444' }}>*</span></label>
                          <input type="text" value={imapHost} onChange={e => { setImapHost(e.target.value); setEmailTestStatus('idle'); }} placeholder="mail.yourdomain.com" style={S.input} />
                        </div>
                        <div>
                          <label style={S.label}>Port</label>
                          <input type="number" value={imapPort} onChange={e => setImapPort(Number(e.target.value))} style={S.input} />
                        </div>
                        <div>
                          <label style={S.label}>SMTP Host <span style={{ color: '#ef4444' }}>*</span></label>
                          <input type="text" value={smtpHost} onChange={e => setSmtpHost(e.target.value)} placeholder="mail.yourdomain.com" style={S.input} />
                        </div>
                        <div>
                          <label style={S.label}>Port</label>
                          <input type="number" value={smtpPort} onChange={e => setSmtpPort(Number(e.target.value))} style={S.input} />
                        </div>
                      </div>
                    )}

                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '8px' }}>
                      <button onClick={handleEmailTest} disabled={!emailUser || !emailPass || (emailPreset === 'cpanel' && (!imapHost || !smtpHost)) || emailTestStatus === 'testing'}
                        style={{ padding: '10px 22px', background: emailTestStatus === 'testing' ? 'var(--bg-element)' : 'linear-gradient(135deg,#5B4FE8,#7c3aed)', color: emailTestStatus === 'testing' ? 'var(--text-faint)' : 'white', border: 'none', borderRadius: '9px', fontWeight: '600', fontSize: '13px', cursor: emailTestStatus === 'testing' ? 'not-allowed' : 'pointer', flexShrink: 0 }}>
                        {emailTestStatus === 'testing' ? 'Testing…' : '🔌 Test IMAP Connection'}
                      </button>
                      {emailTestStatus === 'ok' && <span style={{ color: '#10b981', fontWeight: '600', fontSize: '13px' }}>✓ Connected!</span>}
                      {emailTestStatus === 'error' && <span style={{ color: '#ef4444', fontSize: '12px' }}>{emailTestError}</span>}
                    </div>
                  </div>
                )}

                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '20px' }}>
                  <button onClick={() => goBack(2)} style={S.btnGhost}>← Back</button>
                  <button onClick={() => goNext(2)} disabled={!canProceedStep2} style={S.btnPrimary(!canProceedStep2)}>Continue →</button>
                </div>
              </div>
            )}

            {/* STEP 3: Business details */}
            {step === 3 && (
              <div>
                <div style={{ color: 'var(--text-primary)', fontSize: '18px', fontWeight: '600', marginBottom: '4px' }}>
                  {isEmailBot ? 'Set up your Email bot' : isAIFlow ? 'Set up your AI bot' : 'Tell us about your business'}
                </div>
                <div style={{ color: 'var(--text-faint)', fontSize: '13px', marginBottom: '20px' }}>
                  {isEmailBot ? 'Add a business name and AI key — your bot will auto-reply to emails' : isAIFlow ? 'A few details and your AI bot will be live' : botCategory === 'whatsapp' && whatsappMode === 'own_number' ? 'We use this to register your number with Meta' : 'Basic info to get your bot set up'}
                </div>

                {setupFee > 0 && (
                  <div style={{ background: '#f59e0b0d', border: '1px solid #f59e0b30', borderLeft: '3px solid #f59e0b', borderRadius: '10px', padding: '14px 18px', marginBottom: '20px', display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                    <span style={{ fontSize: '22px', flexShrink: 0 }}>🧾</span>
                    <div>
                      <div style={{ color: '#fbbf24', fontWeight: '600', fontSize: '14px', marginBottom: '3px' }}>One-time setup fee: ${setupFee}</div>
                      <div style={{ color: 'var(--text-secondary)', fontSize: '12px', lineHeight: '1.6' }}>An invoice for ${setupFee} will be created after you click "Create Bot". You'll be redirected to Billing to pay — your bot won't activate until payment is confirmed.</div>
                    </div>
                  </div>
                )}

                <div style={S.card}>
                  <div style={{ display: 'grid', gridTemplateColumns: isAIFlow ? '1fr' : '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                    <div>
                      <label style={S.label}>Business Name <span style={{ color: '#ef4444' }}>*</span></label>
                      <input type="text" value={businessName} onChange={e => setBusinessName(e.target.value)} placeholder="e.g. Acme Hair Salon" style={S.input} />
                    </div>
                    {!isAIFlow && (
                      <div>
                        <label style={S.label}>Industry <span style={{ color: '#ef4444' }}>*</span></label>
                        <select value={industry} onChange={e => setIndustry(e.target.value)} style={{ ...S.input, appearance: 'none' }}>
                          <option value="">Select your industry</option>
                          {industries.map(i => <option key={i.id} value={i.id}>{i.name}</option>)}
                        </select>
                      </div>
                    )}
                  </div>
                  <div style={{ marginBottom: '20px' }}>
                    <label style={S.label}>Welcome Message</label>
                    <textarea value={welcomeMessage} onChange={e => setWelcomeMessage(e.target.value)} rows={2} placeholder={`Welcome to ${businessName || 'our business'}! How can I help you today?`} style={{ ...S.input, resize: 'vertical' }} />
                  </div>

                  <div style={{ borderTop: '1px solid var(--bg-element)', margin: '20px 0' }} />


                  {botCategory === 'whatsapp' && whatsappMode === 'own_number' && (<>
                    <div style={{ color: 'var(--text-primary)', fontWeight: '600', fontSize: '14px', marginBottom: '14px' }}>Your WhatsApp number & business details</div>
                    <InfoBox items={['We submit your details to Meta to get WhatsApp Business API access', 'Your number must NOT already be registered on personal WhatsApp', 'Approval takes 1–3 business days']} color="#10b981" />
                    <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '12px', marginBottom: '16px' }}>
                      <div>
                        <label style={S.label}>Country</label>
                        <select value={ownPhoneCountry} onChange={e => setOwnPhoneCountry(e.target.value)} style={{ ...S.input, appearance: 'none' }}>
                          <option value="ZW">🇿🇼 +263</option>
                          <option value="ZA">🇿🇦 +27</option>
                          <option value="NG">🇳🇬 +234</option>
                          <option value="KE">🇰🇪 +254</option>
                          <option value="US">🇺🇸 +1</option>
                          <option value="GB">🇬🇧 +44</option>
                        </select>
                      </div>
                      <div>
                        <label style={S.label}>Phone Number <span style={{ color: '#ef4444' }}>*</span></label>
                        <input type="tel" value={ownPhoneNumber} onChange={e => setOwnPhoneNumber(e.target.value)} placeholder="77 123 4567" style={S.input} />
                      </div>
                    </div>
                    <div style={{ marginBottom: '16px' }}>
                      <label style={S.label}>Business Address</label>
                      <input type="text" value={businessAddress} onChange={e => setBusinessAddress(e.target.value)} placeholder="123 Main St, Harare, Zimbabwe" style={S.input} />
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                      <div>
                        <label style={S.label}>Business Email <span style={{ color: '#ef4444' }}>*</span></label>
                        <input type="email" value={contactEmail} onChange={e => setContactEmail(e.target.value)} placeholder="you@business.com" style={S.input} />
                      </div>
                      <div>
                        <label style={S.label}>Website (optional)</label>
                        <input type="url" value={websiteUrl} onChange={e => setWebsiteUrl(e.target.value)} placeholder="https://yourbusiness.com" style={S.input} />
                      </div>
                    </div>
                  </>)}

                  {botCategory === 'whatsapp' && whatsappMode === 'self_setup' && (<>
                    <div style={{ color: 'var(--text-primary)', fontWeight: '600', fontSize: '14px', marginBottom: '14px' }}>Your Meta API credentials</div>
                    <InfoBox items={['Find your Phone Number ID in Meta Business Suite → WhatsApp → API Setup', 'Generate a permanent System User Access Token in Meta Business Manager', 'Ensure the token has whatsapp_business_messaging permission']} color="#5B4FE8" />
                    <div style={{ marginBottom: '16px' }}>
                      <label style={S.label}>Phone Number ID <span style={{ color: '#ef4444' }}>*</span></label>
                      <input type="text" value={selfPhoneNumberId} onChange={e => setSelfPhoneNumberId(e.target.value)} placeholder="12345678901234" style={S.input} />
                    </div>
                    <div>
                      <label style={S.label}>Access Token <span style={{ color: '#ef4444' }}>*</span></label>
                      <input type="password" value={selfAccessToken} onChange={e => setSelfAccessToken(e.target.value)} placeholder="EAABkzp..." style={S.input} />
                      <div style={{ color: 'var(--text-faint)', fontSize: '11px', marginTop: '5px' }}>Use a System User token — it won't expire</div>
                    </div>
                  </>)}

                  {botCategory === 'whatsapp' && whatsappMode === 'meta_connect' && (
                    <div style={{ padding: '12px 14px', background: '#10b98110', border: '1px solid #10b98130', borderRadius: '9px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <span style={{ color: '#10b981', fontSize: '18px' }}>✓</span>
                      <span style={{ color: '#6ee7b7', fontSize: '13px' }}>
                        WhatsApp number connected: <strong>{waNumbers.find(n => n.id === selfPhoneNumberId)?.display_phone_number || selfPhoneNumberId}</strong>
                      </span>
                    </div>
                  )}

                  {botCategory === 'facebook' && facebookMode === 'oauth' && (
                    <div style={{ padding: '12px 14px', background: '#10b98110', border: '1px solid #10b98130', borderRadius: '9px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <span style={{ color: '#10b981', fontSize: '18px' }}>✓</span>
                      <span style={{ color: '#6ee7b7', fontSize: '13px' }}>
                        Facebook Page connected: <strong>{fbOAuthPages.find(p => p.id === fbPageId)?.name || fbPageId}</strong>
                      </span>
                    </div>
                  )}

                  {botCategory === 'facebook' && facebookMode === 'we_setup' && (<>
                    <div style={{ color: 'var(--text-primary)', fontWeight: '600', fontSize: '14px', marginBottom: '14px' }}>Your Facebook Page details</div>
                    <InfoBox items={['Our team will connect your page and configure the bot', 'We just need your page name or URL to get started', 'Setup typically completes within 1–2 business days']} color="#10b981" />
                    <div style={{ marginBottom: '16px' }}>
                      <label style={S.label}>Facebook Page Name or URL</label>
                      <input type="text" value={fbPageUrl} onChange={e => setFbPageUrl(e.target.value)} placeholder="facebook.com/yourbusiness or 'Acme Salon'" style={S.input} />
                    </div>
                    <div>
                      <label style={S.label}>Your Email <span style={{ color: '#ef4444' }}>*</span></label>
                      <input type="email" value={contactEmail} onChange={e => setContactEmail(e.target.value)} placeholder="you@business.com" style={S.input} />
                      <div style={{ color: 'var(--text-faint)', fontSize: '11px', marginTop: '5px' }}>Our team will contact you here to arrange access</div>
                    </div>
                  </>)}

                  {botCategory === 'facebook' && facebookMode === 'self_setup' && (<>
                    <div style={{ color: 'var(--text-primary)', fontWeight: '600', fontSize: '14px', marginBottom: '14px' }}>Your Facebook Page credentials</div>
                    <InfoBox items={['Find your Page ID in Facebook Page Settings → About', 'Generate a Page Access Token in Meta Developer Console', 'The token needs pages_messaging permission']} color="#f59e0b" />
                    <div style={{ marginBottom: '16px' }}>
                      <label style={S.label}>Facebook Page ID <span style={{ color: '#ef4444' }}>*</span></label>
                      <input type="text" value={fbPageId} onChange={e => setFbPageId(e.target.value)} placeholder="123456789012345" style={S.input} />
                    </div>
                    <div style={{ marginBottom: '16px' }}>
                      <label style={S.label}>Page Access Token <span style={{ color: '#ef4444' }}>*</span></label>
                      <input type="password" value={fbPageToken} onChange={e => setFbPageToken(e.target.value)} placeholder="EAABkzp..." style={S.input} />
                    </div>
                    <div>
                      <label style={S.label}>Your Email</label>
                      <input type="email" value={contactEmail} onChange={e => setContactEmail(e.target.value)} placeholder="you@business.com" style={S.input} />
                    </div>
                  </>)}

                  {(botCategory === 'website' || botCategory === 'voice') && (<>
                    <div style={{ color: 'var(--text-primary)', fontWeight: '600', fontSize: '14px', marginBottom: '14px' }}>{botCategory === 'voice' ? 'Voice bot setup' : 'Website details'}</div>
                    {botCategory === 'voice' && <InfoBox items={['Our team configures your voice bot and sends you an embed snippet', 'The bot learns from your website content', 'Setup takes 2–5 business days']} color="#f59e0b" />}
                    <div style={{ marginBottom: '16px' }}>
                      <label style={S.label}>Website URL <span style={{ color: '#ef4444' }}>*</span></label>
                      <input type="url" value={websiteUrl} onChange={e => setWebsiteUrl(e.target.value)} placeholder="https://yourbusiness.com" style={S.input} />
                    </div>
                    <div>
                      <label style={S.label}>Your Email <span style={{ color: '#ef4444' }}>*</span></label>
                      <input type="email" value={contactEmail} onChange={e => setContactEmail(e.target.value)} placeholder="you@business.com" style={S.input} />
                      <div style={{ color: 'var(--text-faint)', fontSize: '11px', marginTop: '5px' }}>We'll send your embed code here</div>
                    </div>
                  </>)}

                  {/* ── Email Bot details ──────────────────────────────────── */}
                  {isEmailBot && (<>
                    <div style={{ color: 'var(--text-primary)', fontWeight: '600', fontSize: '14px', marginBottom: '14px' }}>
                      📧 Email Bot — {emailUser || 'your inbox'}
                    </div>
                    <InfoBox items={[
                      'Your bot will automatically reply to new incoming emails using AI',
                      'Pick a dataset for it to answer from — you can change this anytime in Bot Settings',
                      'Connected inbox: ' + (emailUser || '(none set)'),
                    ]} color="#10b981" />
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                        <label style={{ ...S.label, marginBottom: 0 }}>Knowledge Base Dataset <span style={{ color: 'var(--text-faint)', textTransform: 'none', fontWeight: 400 }}>(optional)</span></label>
                        <button type="button" onClick={loadDatasets} style={{ background: 'none', border: 'none', color: '#818cf8', cursor: 'pointer', fontSize: '11px' }}>↻ Refresh</button>
                      </div>
                      {datasetsLoading ? (
                        <div style={{ color: 'var(--text-faint)', fontSize: '12px' }}>Loading datasets…</div>
                      ) : datasets.length === 0 ? (
                        <div style={{ background: 'var(--bg-element)', borderRadius: '8px', padding: '12px', fontSize: '12px', color: 'var(--text-muted)' }}>
                          No datasets yet.{' '}
                          <a href="/datasets" target="_blank" rel="noopener noreferrer" style={{ color: '#818cf8' }}>Upload one</a>{' '}
                          then hit Refresh.
                        </div>
                      ) : (
                        <select value={selectedDatasetId} onChange={e => setSelectedDatasetId(e.target.value)} style={S.input}>
                          <option value="">— Add later in Bot Settings —</option>
                          {datasets.map(d => <option key={d.id} value={d.id}>{d.name} ({d.format})</option>)}
                        </select>
                      )}
                    </div>
                  </>)}

                  {/* ── AI Bot details ──────────────────────────────────────── */}
                  {isAIFlow && (<>
                    <div style={{ color: 'var(--text-primary)', fontWeight: '600', fontSize: '14px', marginBottom: '14px' }}>
                      🤖 AI Bot — {aiChannel === 'whatsapp' ? 'WhatsApp' : aiChannel === 'facebook' ? 'Facebook' : 'Website'}
                    </div>
                    <InfoBox items={[
                      'Your bot will answer every message automatically using AI',
                      'Pick a dataset below for it to answer from — you can change this anytime in Bot Settings',
                      'You can also update the tone and system prompt any time in Settings',
                    ]} color="#a855f7" />

                    <div style={{ marginBottom: '14px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                        <label style={{ ...S.label, marginBottom: 0 }}>Knowledge Base Dataset <span style={{ color: 'var(--text-faint)', textTransform: 'none', fontWeight: 400 }}>(optional — add later if you prefer)</span></label>
                        <button type="button" onClick={loadDatasets} style={{ background: 'none', border: 'none', color: '#818cf8', cursor: 'pointer', fontSize: '11px' }}>↻ Refresh</button>
                      </div>
                      {datasetsLoading ? (
                        <div style={{ color: 'var(--text-faint)', fontSize: '12px' }}>Loading datasets…</div>
                      ) : datasets.length === 0 ? (
                        <div style={{ background: 'var(--bg-element)', borderRadius: '8px', padding: '12px', fontSize: '12px', color: 'var(--text-muted)' }}>
                          No datasets yet.{' '}
                          <a href="/datasets" target="_blank" rel="noopener noreferrer" style={{ color: '#818cf8' }}>Upload one</a>{' '}
                          (opens in a new tab — your progress here is kept), then hit Refresh.
                        </div>
                      ) : (
                        <select value={selectedDatasetId} onChange={e => setSelectedDatasetId(e.target.value)} style={S.input}>
                          <option value="">— Add later in Bot Settings —</option>
                          {datasets.map(d => <option key={d.id} value={d.id}>{d.name} ({d.format})</option>)}
                        </select>
                      )}
                    </div>

                    {aiChannel === 'whatsapp' && (<>
                      <div style={{ color: 'var(--text-primary)', fontWeight: '600', fontSize: '13px', marginBottom: '10px' }}>WhatsApp Business API credentials</div>
                      <InfoBox items={['Find your Phone Number ID in Meta Business Suite → WhatsApp → API Setup', 'Use a permanent System User Access Token from Meta Business Manager']} color="#10b981" />
                      <div style={{ marginBottom: '14px' }}>
                        <label style={S.label}>Phone Number ID <span style={{ color: '#ef4444' }}>*</span></label>
                        <input type="text" value={selfPhoneNumberId} onChange={e => setSelfPhoneNumberId(e.target.value)} placeholder="12345678901234" style={S.input} />
                      </div>
                      <div>
                        <label style={S.label}>Access Token <span style={{ color: '#ef4444' }}>*</span></label>
                        <input type="password" value={selfAccessToken} onChange={e => setSelfAccessToken(e.target.value)} placeholder="EAABkzp..." style={S.input} />
                      </div>
                    </>)}

                    {aiChannel === 'facebook' && (<>
                      <div style={{ color: 'var(--text-primary)', fontWeight: '600', fontSize: '13px', marginBottom: '10px' }}>Facebook Page credentials</div>
                      <InfoBox items={['Find your Page ID in Facebook Page Settings → About', 'Generate a Page Access Token in Meta Developer Console with pages_messaging permission']} color="#3b82f6" />
                      <div style={{ marginBottom: '14px' }}>
                        <label style={S.label}>Facebook Page ID <span style={{ color: '#ef4444' }}>*</span></label>
                        <input type="text" value={fbPageId} onChange={e => setFbPageId(e.target.value)} placeholder="123456789012345" style={S.input} />
                      </div>
                      <div>
                        <label style={S.label}>Page Access Token <span style={{ color: '#ef4444' }}>*</span></label>
                        <input type="password" value={fbPageToken} onChange={e => setFbPageToken(e.target.value)} placeholder="EAABkzp..." style={S.input} />
                      </div>
                    </>)}

                    {aiChannel === 'website' && (
                      <div>
                        <label style={S.label}>Your Email <span style={{ color: '#ef4444' }}>*</span></label>
                        <input type="email" value={contactEmail} onChange={e => setContactEmail(e.target.value)} placeholder="you@business.com" style={S.input} />
                        <div style={{ color: 'var(--text-faint)', fontSize: '11px', marginTop: '5px' }}>We'll send your widget embed code here</div>
                      </div>
                    )}
                  </>)}
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '20px' }}>
                  <button onClick={() => goBack(3)} style={S.btnGhost}>← Back</button>
                  <button onClick={() => (isAIFlow || isEmailBot) ? createBot() : goNext(3)} disabled={!canProceedStep3 || loading} style={S.btnPrimary(!canProceedStep3 || loading)}>
                    {isEmailBot ? (loading ? 'Creating...' : '📧 Create Email Bot') : isAIFlow ? (loading ? 'Creating...' : '✨ Create AI Bot') : 'Continue →'}
                  </button>
                </div>
              </div>
            )}

            {/* STEP 4: Template */}
            {step === 4 && (
              <div>
                <div style={{ color: 'var(--text-primary)', fontSize: '18px', fontWeight: '600', marginBottom: '4px' }}>Pick a starting template</div>
                <div style={{ color: 'var(--text-faint)', fontSize: '13px', marginBottom: '24px' }}>Pre-filled with smart replies for your industry. You can edit everything after.</div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(190px,1fr))', gap: '12px', marginBottom: '28px' }}>
                  {/* Built-in templates */}
                  {industries.map((ind) => {
                    const t = INDUSTRY_TEMPLATES[ind.id];
                    const sel = selectedTemplate === ind.id;
                    return (
                      <div key={ind.id} onClick={() => setSelectedTemplate(ind.id)} style={{ padding: '18px', border: sel ? '2px solid #5B4FE8' : '1px solid var(--bg-element)', borderRadius: '13px', cursor: 'pointer', background: sel ? '#5B4FE810' : 'var(--bg-card)', transition: 'all 0.15s', textAlign: 'center' }}>
                        <div style={{ fontSize: '32px', marginBottom: '8px' }}>{t?.icon || '🤖'}</div>
                        <div style={{ color: 'var(--text-primary)', fontWeight: '600', fontSize: '13px', marginBottom: '3px' }}>{ind.name.replace(/^[^ ]+ /, '')}</div>
                        <div style={{ color: 'var(--text-faint)', fontSize: '11px' }}>{ind.description}</div>
                        {sel && <div style={{ color: '#5B4FE8', fontSize: '11px', fontWeight: '700', marginTop: '8px' }}>✓ Selected</div>}
                      </div>
                    );
                  })}

                  {/* Firebase templates */}
                  {firebaseTemplates.map((t) => {
                    const sel = selectedTemplate === t.id;
                    const isOwn = auth.currentUser && t.createdBy === auth.currentUser.uid;
                    return (
                      <div key={t.id} onClick={() => setSelectedTemplate(t.id)} style={{ padding: '18px', border: sel ? '2px solid #5B4FE8' : '1px solid var(--bg-element)', borderRadius: '13px', cursor: 'pointer', background: sel ? '#5B4FE810' : 'var(--bg-card)', transition: 'all 0.15s', textAlign: 'center', position: 'relative' }}>
                        {isOwn && !t.isPublic && <div style={{ position: 'absolute', top: '8px', right: '8px', background: '#10b98115', color: '#10b981', fontSize: '9px', fontWeight: '700', padding: '2px 6px', borderRadius: '8px' }}>🔒 Mine</div>}
                        <div style={{ fontSize: '32px', marginBottom: '8px' }}>{t.icon || '🤖'}</div>
                        <div style={{ color: 'var(--text-primary)', fontWeight: '600', fontSize: '13px', marginBottom: '3px' }}>{t.name}</div>
                        <div style={{ color: 'var(--text-faint)', fontSize: '11px' }}>{t.description || `${t.triggers?.length || 0} triggers`}</div>
                        {sel && <div style={{ color: '#5B4FE8', fontSize: '11px', fontWeight: '700', marginTop: '8px' }}>✓ Selected</div>}
                      </div>
                    );
                  })}
                </div>

                <div style={{ background: '#5B4FE810', border: '1px solid #5B4FE830', borderRadius: '13px', padding: '20px', marginBottom: '24px', display: 'flex', gap: '14px' }}>
                  <span style={{ fontSize: '20px' }}>✨</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ color: 'var(--text-primary)', fontWeight: '600', marginBottom: '10px', fontSize: '14px' }}>What happens after you create</div>
                    {['Your bot launches with pre-built conversation flows', 'Customise everything in the visual Flow Builder', 'Track conversations and performance in your dashboard'].map((item, i) => (
                      <div key={i} style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', marginBottom: '5px' }}>
                        <span style={{ color: '#5B4FE8', fontSize: '11px', marginTop: '4px' }}>→</span>
                        <span style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>{item}</span>
                      </div>
                    ))}
                    {setupFee > 0 && (
                      <div style={{ marginTop: '14px', padding: '12px 14px', background: '#f59e0b0d', border: '1px solid #f59e0b30', borderRadius: '9px', display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                        <span style={{ fontSize: '16px' }}>🧾</span>
                        <div>
                          <div style={{ color: '#fbbf24', fontWeight: '600', fontSize: '13px', marginBottom: '2px' }}>Invoice will be created: ${setupFee}</div>
                          <div style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>You'll be taken to Billing to pay the setup invoice. Your bot activates once payment is confirmed.</div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <button onClick={() => goBack(4)} style={S.btnGhost}>← Back</button>
                  <button onClick={createBot} disabled={loading} style={S.btnPrimary(loading)}>
                    {loading ? 'Creating...' : setupFee > 0 ? `✨ Create Bot & Generate Invoice ($${setupFee})` : '✨ Create Bot'}
                  </button>
                </div>
              </div>
            )}

          </div>
        </div>
      </div>
    </>
  );
}
